import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { Table as TableIcon, BarChart3, Maximize2, Minimize2 } from "lucide-react";
import { api, fmtMXN, fmtDate, todayISO } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const CAR_COLORS = ["#0F172A", "#0EA5E9", "#F59E0B", "#E11D48", "#10B981", "#8B5CF6", "#EC4899", "#64748B"];
const CHART_TICK_STYLE = { fontSize: 11, fill: "#64748B" };
const CHART_TOOLTIP_STYLE = { borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 };
const CHART_LEGEND_STYLE = { fontSize: 11 };

export default function ExecutiveSummary() {
    const [ytd, setYtd] = useState(null);
    const [month, setMonth] = useState(null);
    const [months, setMonths] = useState([]);
    const [loading, setLoading] = useState(true);

    const today = useMemo(() => todayISO(), []);
    const currentYear = useMemo(() => today.slice(0, 4), [today]);
    const monthStart = useMemo(() => `${today.slice(0, 8)}01`, [today]);

    const load = useCallback(async () => {
        setLoading(true);
        const [ytdRes, monthRes, monthlyRes] = await Promise.all([
            api.get("/analytics/summary", { params: { start_date: `${currentYear}-01-01`, end_date: today } }),
            api.get("/analytics/summary", { params: { start_date: monthStart, end_date: today } }),
            api.get("/analytics/monthly"),
        ]);
        setYtd(ytdRes.data);
        setMonth(monthRes.data);
        setMonths(monthlyRes.data.months || []);
        setLoading(false);
    }, [currentYear, today, monthStart]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className="space-y-6" data-testid="executive-summary-page">
            <header>
                <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Panel ejecutivo</p>
                <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mt-1 font-display">
                    Resumen Ejecutivo
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    Vista consolidada de ingresos y gastos: acumulado del año, mes actual y desglose por mes.
                </p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                <PeriodSummaryCard
                    title="Acumulado del año (YTD)"
                    subtitle={`1 de enero ${currentYear} → ${fmtDate(today)}`}
                    data={ytd}
                    loading={loading}
                    testid="ytd-summary-card"
                />
                <PeriodSummaryCard
                    title="Mes actual"
                    subtitle={`${fmtDate(monthStart)} → ${fmtDate(today)}`}
                    data={month}
                    loading={loading}
                    testid="month-summary-card"
                />
                <MonthlySummaryCard year={currentYear} months={months} loading={loading} />
            </div>
        </div>
    );
}

function ToggleGroup({ options, value, onChange, testidPrefix }) {
    return (
        <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5">
            {options.map((opt) => (
                <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange(opt.value)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                        value === opt.value ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid={`${testidPrefix}-${opt.value}`}
                >
                    {opt.icon}
                    {opt.label}
                </button>
            ))}
        </div>
    );
}

function FullscreenButton({ active, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 shrink-0 transition-colors"
            data-testid={testid}
            aria-label={active ? "Salir de pantalla completa" : "Ver en pantalla completa"}
        >
            {active ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
    );
}

function CardToolbar({ view, setView, scope, setScope, testid }) {
    return (
        <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
            <ToggleGroup
                options={[
                    { value: "table", label: "Tabla", icon: <TableIcon size={12} /> },
                    { value: "chart", label: "Gráfica", icon: <BarChart3 size={12} /> },
                ]}
                value={view}
                onChange={setView}
                testidPrefix={`${testid}-view`}
            />
            <ToggleGroup
                options={[
                    { value: "general", label: "General" },
                    { value: "porauto", label: "Por auto" },
                ]}
                value={scope}
                onChange={setScope}
                testidPrefix={`${testid}-scope`}
            />
        </div>
    );
}

function totalsBarChart(rows) {
    return (
        <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="label" tick={CHART_TICK_STYLE} />
                    <YAxis tick={CHART_TICK_STYLE} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => fmtMXN(v)} contentStyle={CHART_TOOLTIP_STYLE} />
                    <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                    <Bar dataKey="income" name="Ingresos" fill="#10B981" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="expense" name="Gastos" fill="#E11D48" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="net" name="Neto" fill="#0F172A" radius={[6, 6, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function PeriodSummaryCard({ title, subtitle, data, loading, testid }) {
    const [view, setView] = useState("table");
    const [scope, setScope] = useState("general");
    const [fullscreen, setFullscreen] = useState(false);
    const byCar = useMemo(
        () => (data?.by_car || []).slice().sort((a, b) => a.label.localeCompare(b.label, "es")),
        [data]
    );

    return (
        <>
            {fullscreen && (
                <div
                    className="fixed inset-0 z-40 bg-slate-900/40"
                    onClick={() => setFullscreen(false)}
                    data-testid={`${testid}-fullscreen-backdrop`}
                />
            )}
            <Card
                className={fullscreen ? "border-slate-200 shadow-2xl fixed inset-4 md:inset-10 z-50 overflow-y-auto" : "border-slate-200 shadow-sm"}
                data-testid={testid}
            >
                <CardHeader className="space-y-1">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <CardTitle className="text-lg font-display font-medium">{title}</CardTitle>
                            <p className="text-xs text-slate-500">{subtitle}</p>
                        </div>
                        <FullscreenButton active={fullscreen} onClick={() => setFullscreen((f) => !f)} testid={`${testid}-fullscreen-toggle`} />
                    </div>
                    <CardToolbar view={view} setView={setView} scope={scope} setScope={setScope} testid={testid} />
                </CardHeader>
                <CardContent>
                {loading ? (
                    <div className="h-40 grid place-items-center text-slate-400 text-sm">Cargando…</div>
                ) : scope === "general" ? (
                    view === "table" ? (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                    <th className="text-left px-3 py-2 font-semibold">Ingresos</th>
                                    <th className="text-left px-3 py-2 font-semibold">Gastos</th>
                                    <th className="text-left px-3 py-2 font-semibold">Neto</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-t border-slate-100">
                                    <td className="px-3 py-3 text-emerald-600 font-semibold">{fmtMXN(data?.total_income || 0)}</td>
                                    <td className="px-3 py-3 text-rose-600 font-semibold">{fmtMXN(data?.total_expense || 0)}</td>
                                    <td className={`px-3 py-3 font-semibold ${(data?.net || 0) >= 0 ? "text-slate-900" : "text-rose-600"}`}>
                                        {fmtMXN(data?.net || 0)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    ) : (
                        totalsBarChart([{ label: "Total", income: data?.total_income || 0, expense: data?.total_expense || 0, net: data?.net || 0 }])
                    )
                ) : byCar.length === 0 ? (
                    <div className="h-32 grid place-items-center text-slate-400 text-sm">Sin datos</div>
                ) : view === "table" ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                    <th className="text-left px-3 py-2 font-semibold">Auto</th>
                                    <th className="text-right px-3 py-2 font-semibold">Ingresos</th>
                                    <th className="text-right px-3 py-2 font-semibold">Gastos</th>
                                    <th className="text-right px-3 py-2 font-semibold">Neto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {byCar.map((b) => (
                                    <tr key={b.car_id} className="border-t border-slate-100" data-testid={`${testid}-row-${b.car_id}`}>
                                        <td className="px-3 py-2.5 text-slate-900 font-medium">{b.label}</td>
                                        <td className="px-3 py-2.5 text-right text-emerald-600">{fmtMXN(b.income)}</td>
                                        <td className="px-3 py-2.5 text-right text-rose-600">{fmtMXN(b.expense)}</td>
                                        <td className={`px-3 py-2.5 text-right font-medium ${b.net >= 0 ? "text-slate-900" : "text-rose-600"}`}>
                                            {fmtMXN(b.net)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    totalsBarChart(byCar)
                )}
                </CardContent>
            </Card>
        </>
    );
}

function MonthlySummaryCard({ year, months, loading }) {
    const [view, setView] = useState("table");
    const [scope, setScope] = useState("general");
    const [fullscreen, setFullscreen] = useState(false);

    const allCars = useMemo(() => {
        const map = {};
        months.forEach((m) => (m.by_car || []).forEach((b) => { map[b.car_id] = b.label; }));
        return Object.entries(map)
            .map(([car_id, label]) => ({ car_id, label }))
            .sort((a, b) => a.label.localeCompare(b.label, "es"));
    }, [months]);

    const perCarChartData = useMemo(() => {
        return months.map((m) => {
            const row = { label: m.label };
            allCars.forEach((c) => {
                const found = (m.by_car || []).find((b) => b.car_id === c.car_id);
                row[c.car_id] = found ? found.net : 0;
            });
            return row;
        });
    }, [months, allCars]);

    const groups = months.filter((m) => (m.by_car || []).length > 0);

    return (
        <>
            {fullscreen && (
                <div
                    className="fixed inset-0 z-40 bg-slate-900/40"
                    onClick={() => setFullscreen(false)}
                    data-testid="monthly-summary-card-fullscreen-backdrop"
                />
            )}
            <Card
                className={fullscreen ? "border-slate-200 shadow-2xl fixed inset-4 md:inset-10 z-50 overflow-y-auto" : "border-slate-200 shadow-sm lg:col-span-1"}
                data-testid="monthly-summary-card"
            >
                <CardHeader className="space-y-1">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <CardTitle className="text-lg font-display font-medium">Resumen mensual {year}</CardTitle>
                            <p className="text-xs text-slate-500">Ingresos y gastos de cada mes del año en curso.</p>
                        </div>
                        <FullscreenButton active={fullscreen} onClick={() => setFullscreen((f) => !f)} testid="monthly-summary-card-fullscreen-toggle" />
                    </div>
                    <CardToolbar view={view} setView={setView} scope={scope} setScope={setScope} testid="monthly-summary-card" />
                </CardHeader>
                <CardContent>
                {loading ? (
                    <div className="h-40 grid place-items-center text-slate-400 text-sm">Cargando…</div>
                ) : months.length === 0 ? (
                    <div className="h-32 grid place-items-center text-slate-400 text-sm">Sin datos</div>
                ) : scope === "general" ? (
                    view === "table" ? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                        <th className="text-left px-3 py-2 font-semibold">Mes</th>
                                        <th className="text-right px-3 py-2 font-semibold">Ingresos</th>
                                        <th className="text-right px-3 py-2 font-semibold">Gastos</th>
                                        <th className="text-right px-3 py-2 font-semibold">Neto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {months.map((m) => (
                                        <tr key={m.month} className="border-t border-slate-100" data-testid={`monthly-row-${m.month}`}>
                                            <td className="px-3 py-2.5 text-slate-900 font-medium">{m.label}</td>
                                            <td className="px-3 py-2.5 text-right text-emerald-600">{fmtMXN(m.income)}</td>
                                            <td className="px-3 py-2.5 text-right text-rose-600">{fmtMXN(m.expense)}</td>
                                            <td className={`px-3 py-2.5 text-right font-medium ${m.net >= 0 ? "text-slate-900" : "text-rose-600"}`}>
                                                {fmtMXN(m.net)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        totalsBarChart(months)
                    )
                ) : groups.length === 0 ? (
                    <div className="h-32 grid place-items-center text-slate-400 text-sm">Sin datos por auto todavía</div>
                ) : view === "table" ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                    <th className="text-left px-3 py-2 font-semibold">Auto</th>
                                    <th className="text-right px-3 py-2 font-semibold">Ingresos</th>
                                    <th className="text-right px-3 py-2 font-semibold">Gastos</th>
                                    <th className="text-right px-3 py-2 font-semibold">Neto</th>
                                </tr>
                            </thead>
                            {groups.map((m, idx) => (
                                <tbody key={m.month} className={idx > 0 ? "border-t-8 border-slate-100" : undefined} data-testid={`monthly-group-${m.month}`}>
                                    <tr className="bg-slate-100/70">
                                        <td colSpan={3} className="px-3 py-2 font-semibold text-slate-800">{m.label}</td>
                                        <td className="px-3 py-2 text-right font-semibold text-slate-800">{fmtMXN(m.net)}</td>
                                    </tr>
                                    {m.by_car.map((b) => (
                                        <tr key={b.car_id} className="border-t border-slate-100">
                                            <td className="px-3 py-2 text-slate-700">{b.label}</td>
                                            <td className="px-3 py-2 text-right text-emerald-600">{fmtMXN(b.income)}</td>
                                            <td className="px-3 py-2 text-right text-rose-600">{fmtMXN(b.expense)}</td>
                                            <td className={`px-3 py-2 text-right font-medium ${b.net >= 0 ? "text-slate-900" : "text-rose-600"}`}>
                                                {fmtMXN(b.net)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            ))}
                        </table>
                    </div>
                ) : (
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={perCarChartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="label" tick={CHART_TICK_STYLE} />
                                <YAxis tick={CHART_TICK_STYLE} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                <Tooltip formatter={(v) => fmtMXN(v)} contentStyle={CHART_TOOLTIP_STYLE} />
                                <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                                {allCars.map((c, idx) => (
                                    <Bar key={c.car_id} dataKey={c.car_id} name={c.label} fill={CAR_COLORS[idx % CAR_COLORS.length]} radius={[6, 6, 0, 0]} />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
                </CardContent>
            </Card>
        </>
    );
}
